import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calculator as CalcIcon, TrendingUp, DollarSign, Percent, Calendar,
  Repeat, ArrowRight, Sparkles, Download, Check, X, ChevronDown, Minus, Plus,
  ShieldCheck, LineChart as LineIcon, BadgePercent, Info, Search, Loader2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import usePageSeo from "@/hooks/usePageSeo";
import AboutDppCard from "@/components/AboutDppCard";
import TickerAutocomplete from "@/components/TickerAutocomplete";

// ------------------------------------------------------------------
// Projection engine (client-side, no backend call). Mirrors the
// Snowball Analytics dividend calculator model:
//   Principal = price × shares
//   Each year we grow price, grow dividend-per-share, add annual
//   contributions (buying shares at that year's price), and apply
//   dividends (net of tax). With DRIP on, net dividends buy more
//   shares at the year's price. Optional inflation deflates everything
//   back to today's dollars for a "real return" view.
// ------------------------------------------------------------------
function project({
  price, shares, years, yieldPct, annualContribution, drip,
  priceGrowthPct, divGrowthPct, taxPct, inflation,
}) {
  const priceMult = 1 + (priceGrowthPct || 0) / 100;
  const divMult   = 1 + (divGrowthPct || 0) / 100;
  const taxMult   = 1 - Math.max(0, Math.min(100, taxPct || 0)) / 100;
  const inflMult  = inflation ? 1 / 1.03 : 1; // apply cumulatively each year

  const principal = price * shares;

  let sh = shares;
  let px = price;
  let divPerShare = price * (yieldPct / 100); // annual dividend per share
  let totalContribs = 0;
  let totalNetDivs  = 0;
  let inflAdj = 1;

  const rows = [];
  for (let y = 1; y <= years; y++) {
    // 1. Price appreciates during the year
    px = px * priceMult;

    // 2. Dividends paid on shares held at start of year
    const grossDivs = sh * divPerShare;
    const netDivs   = grossDivs * taxMult;
    totalNetDivs   += netDivs;

    if (drip && px > 0) {
      sh += netDivs / px;
    }

    // 3. Annual contribution buys shares at year-end price
    if (annualContribution > 0 && px > 0) {
      sh += annualContribution / px;
      totalContribs += annualContribution;
    }

    // 4. Dividend per share grows for next year
    divPerShare = divPerShare * divMult;

    // Snapshot at end of year
    inflAdj = inflAdj * inflMult;
    const value        = sh * px;
    const annualIncome = sh * divPerShare; // forward-looking next year div income
    const growthOnly   = value - principal - totalContribs - (drip ? totalNetDivs : 0);

    rows.push({
      year: y,
      price: +(px * inflAdj).toFixed(2),
      shares: +sh.toFixed(4),
      value: Math.round(value * inflAdj),
      annualIncome: Math.round(annualIncome * inflAdj),
      monthlyIncome: Math.round((annualIncome / 12) * inflAdj),
      cumulativeDivs: Math.round(totalNetDivs * inflAdj),
      cumulativeContribs: Math.round(totalContribs * inflAdj),
      growth: Math.round(growthOnly * inflAdj),
      principal: Math.round(principal * inflAdj),
      // YoC = forward annual income / cost basis (principal + all contributions)
      yieldOnCost: (principal + totalContribs) > 0 ? +((annualIncome / (principal + totalContribs)) * 100).toFixed(2) : 0,
    });
  }
  return { rows, principal };
}

const fmt = (n) => (n == null || Number.isNaN(n) ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const pct = (n) => (n == null || Number.isNaN(n) ? "—" : `${Number(n).toFixed(2)}%`);

// ------------------------------------------------------------------
// Ticker chip presets — clicking pre-fills the calculator with the
// ticker's most-recent yield / growth / price so a visitor can see
// a plausible projection without knowing the numbers. These are
// static seeds (Feb 2026 snapshot) — DPP's authenticated backtest
// uses actual yfinance data.
// ------------------------------------------------------------------
const TICKER_SEEDS = {
  // Dividend Kings
  KO:   { price: 65,  yieldPct: 3.0, divGrowthPct: 5.0, priceGrowthPct: 6, name: "Coca-Cola" },
  PEP:  { price: 168, yieldPct: 3.4, divGrowthPct: 7.0, priceGrowthPct: 6, name: "PepsiCo" },
  JNJ:  { price: 158, yieldPct: 3.2, divGrowthPct: 5.5, priceGrowthPct: 5, name: "Johnson & Johnson" },
  PG:   { price: 158, yieldPct: 2.5, divGrowthPct: 5.0, priceGrowthPct: 6, name: "Procter & Gamble" },
  LOW:  { price: 240, yieldPct: 2.0, divGrowthPct: 15,  priceGrowthPct: 8, name: "Lowe's" },
  TGT:  { price: 155, yieldPct: 3.1, divGrowthPct: 10,  priceGrowthPct: 6, name: "Target" },
  ABBV: { price: 175, yieldPct: 3.5, divGrowthPct: 7.0, priceGrowthPct: 7, name: "AbbVie" },
  // Tech Yield
  MSFT: { price: 430, yieldPct: 0.7, divGrowthPct: 10,  priceGrowthPct: 12, name: "Microsoft" },
  AVGO: { price: 165, yieldPct: 1.4, divGrowthPct: 14,  priceGrowthPct: 15, name: "Broadcom" },
  CSCO: { price: 62,  yieldPct: 2.6, divGrowthPct: 5.0, priceGrowthPct: 7,  name: "Cisco" },
  TXN:  { price: 195, yieldPct: 2.8, divGrowthPct: 8.0, priceGrowthPct: 7,  name: "Texas Instruments" },
  // High-yield ETFs
  SCHD: { price: 28,  yieldPct: 3.4, divGrowthPct: 11,  priceGrowthPct: 7,  name: "Schwab US Dividend Equity" },
  JEPI: { price: 58,  yieldPct: 7.6, divGrowthPct: 2.0, priceGrowthPct: 4,  name: "JPMorgan Equity Premium Income" },
  JEPQ: { price: 55,  yieldPct: 9.2, divGrowthPct: 2.0, priceGrowthPct: 6,  name: "JPMorgan Nasdaq Equity Premium" },
  QQQI: { price: 21,  yieldPct: 13,  divGrowthPct: 1.0, priceGrowthPct: 5,  name: "NEOS Nasdaq-100 High Income" },
  VYM:  { price: 128, yieldPct: 2.8, divGrowthPct: 6.0, priceGrowthPct: 7,  name: "Vanguard High Dividend Yield" },
  VIG:  { price: 195, yieldPct: 1.8, divGrowthPct: 9.0, priceGrowthPct: 8,  name: "Vanguard Dividend Appreciation" },
  DGRO: { price: 60,  yieldPct: 2.3, divGrowthPct: 9.0, priceGrowthPct: 8,  name: "iShares Core Dividend Growth" },
  QYLD: { price: 18,  yieldPct: 12,  divGrowthPct: 0.5, priceGrowthPct: 2,  name: "Global X Nasdaq Covered Call" },
  // Monthly REITs
  O:    { price: 58,  yieldPct: 5.5, divGrowthPct: 3.0, priceGrowthPct: 4, name: "Realty Income" },
  STAG: { price: 37,  yieldPct: 4.0, divGrowthPct: 3.0, priceGrowthPct: 5, name: "Stag Industrial" },
  MAIN: { price: 55,  yieldPct: 5.6, divGrowthPct: 4.0, priceGrowthPct: 5, name: "Main Street Capital" },
  VICI: { price: 32,  yieldPct: 5.4, divGrowthPct: 6.0, priceGrowthPct: 5, name: "VICI Properties" },
  // Energy
  XOM:  { price: 115, yieldPct: 3.5, divGrowthPct: 4.0, priceGrowthPct: 5, name: "Exxon Mobil" },
  CVX:  { price: 155, yieldPct: 4.3, divGrowthPct: 6.0, priceGrowthPct: 5, name: "Chevron" },
  EPD:  { price: 32,  yieldPct: 7.1, divGrowthPct: 3.0, priceGrowthPct: 4, name: "Enterprise Products" },
  KMI:  { price: 22,  yieldPct: 5.3, divGrowthPct: 2.0, priceGrowthPct: 4, name: "Kinder Morgan" },
  // Blue chip recovery
  PFE:  { price: 27,  yieldPct: 6.2, divGrowthPct: 2.0, priceGrowthPct: 4, name: "Pfizer" },
  BAC:  { price: 43,  yieldPct: 2.6, divGrowthPct: 8.0, priceGrowthPct: 7, name: "Bank of America" },
};

const STRATEGIES = [
  {
    icon: "👑",
    title: "Dividend Kings",
    desc: "50+ consecutive years of uninterrupted dividend growth.",
    tickers: ["KO", "PEP", "JNJ", "PG", "LOW", "TGT", "ABBV"],
  },
  {
    icon: "💻",
    title: "AI & Tech Yield",
    desc: "Sector leaders with rising dividend payouts alongside long-term growth.",
    tickers: ["MSFT", "AVGO", "CSCO", "TXN"],
  },
  {
    icon: "📈",
    title: "High-Yield Income ETFs",
    desc: "Instant diversification — covered calls to dividend growth strategies.",
    tickers: ["JEPI", "JEPQ", "QQQI", "SCHD", "VYM", "VIG", "DGRO", "QYLD"],
  },
  {
    icon: "🏢",
    title: "Monthly Payer REITs",
    desc: "Real estate cash flow delivered monthly. Maximum DRIP compounding frequency.",
    tickers: ["O", "STAG", "MAIN", "VICI"],
  },
  {
    icon: "⚡",
    title: "Energy & Infrastructure",
    desc: "Hard-asset yields backed by pipelines, utilities, and energy majors.",
    tickers: ["XOM", "CVX", "EPD", "KMI"],
  },
  {
    icon: "🔄",
    title: "Blue Chip Recovery",
    desc: "High-yield mega-caps at compressed valuations — outsized current income.",
    tickers: ["PFE", "BAC"],
  },
];

const FAQ_ITEMS = [
  {
    q: "How do I calculate dividend yield accurately?",
    a: "Dividend yield = Annual dividend per share ÷ Current share price × 100. For forward-looking analysis use Forward Yield (based on the most recently declared annualised dividend) rather than Trailing Yield. For ETFs, the 30-day SEC yield is the cleanest number.",
  },
  {
    q: "What is a Dividend Reinvestment Plan (DRIP) and why does it matter?",
    a: "DRIP automatically reinvests your dividend payouts to purchase additional shares rather than paying cash. The compounding effect is dramatic over long horizons — toggling DRIP off in the calculator shows exactly how much value you leave on the table by taking dividends as cash.",
  },
  {
    q: "How does the dividend tax rate input work?",
    a: "Set your effective dividend tax rate from 0% (Roth IRA / TFSA / ISA) to 40% (top marginal, unqualified). The calculator applies this to every dividend payment before it either lands in your pocket or is reinvested via DRIP. Even a 5% drag compounds into a meaningful gap over 20+ years.",
  },
  {
    q: "How do I predict dividend growth (CAGR) for a stock?",
    a: "Dividend CAGR = (Latest annual dividend ÷ Dividend N years ago)^(1/N) − 1. For forward estimates, check payout ratio headroom (40% leaves more growth room than 80%), free cash flow trends, and management's capital-allocation guidance.",
  },
  {
    q: "What's the difference between a Dividend King, Aristocrat, and Achiever?",
    a: "Dividend Kings have increased dividends for 50+ consecutive years (KO, PG, JNJ). Dividend Aristocrats require 25+ years and S&P 500 membership. Dividend Achievers need 10+ consecutive years of growth.",
  },
  {
    q: "Can I track my actual dividend portfolio after using the calculator?",
    a: "Yes — DPP is a full portfolio and dividend tracking platform. After running your forecast, create a free account to run historical backtests against real dividend data, link your brokerage via SnapTrade, and get an AI-written explanation of your portfolio's numbers.",
  },
];

// ------------------------------------------------------------------
// Little +/- number-stepper input (mimics Snowball's UX)
// ------------------------------------------------------------------
function Stepper({ value, onChange, step = 1, min, max, suffix, prefix, testId }) {
  const bump = (dir) => {
    const next = Number(value) + dir * step;
    if (min != null && next < min) return;
    if (max != null && next > max) return;
    onChange(+next.toFixed(4));
  };
  return (
    <div className="flex items-center border border-border rounded-full overflow-hidden bg-background/60">
      <button
        type="button"
        onClick={() => bump(-1)}
        className="w-9 h-9 grid place-items-center text-muted-foreground hover:text-primary hover:bg-muted transition"
        aria-label="Decrease"
      >
        <Minus className="w-3.5 h-3.5" />
      </button>
      <div className="flex-1 relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
          className={`w-full h-9 bg-transparent text-center text-sm outline-none tabular-nums ${prefix ? "pl-6" : ""} ${suffix ? "pr-6" : ""}`}
          data-testid={testId}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{suffix}</span>}
      </div>
      <button
        type="button"
        onClick={() => bump(1)}
        className="w-9 h-9 grid place-items-center text-muted-foreground hover:text-primary hover:bg-muted transition"
        aria-label="Increase"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ------------------------------------------------------------------
// Segmented Yes/No toggle
// ------------------------------------------------------------------
function Segmented({ value, onChange, options, testId }) {
  return (
    <div className="inline-flex rounded-full border border-border bg-background/60 p-0.5 text-xs" data-testid={testId}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          data-testid={testId ? `${testId}-${String(o.label).toLowerCase()}` : undefined}
          onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 rounded-full transition ${
            value === o.value
              ? "bg-primary text-primary-foreground shadow"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
// CSV downloader — grabs the projection rows and lets user save them.
// Enables copy/paste into Excel, keeps parity with Snowball's CSV.
// ------------------------------------------------------------------
function downloadCSV(rows) {
  const header = ["Year", "Portfolio Value", "Annual Dividend", "Monthly Dividend", "Cumulative Dividends", "Share Price", "Shares", "Yield on Cost %"];
  const lines = rows.map(r => [r.year, r.value, r.annualIncome, r.monthlyIncome, r.cumulativeDivs, r.price, r.shares, r.yieldOnCost].join(","));
  const csv = [header.join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "dpp-dividend-projection.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Calculator() {
  const nav = useNavigate();
  usePageSeo({
    title: "Dividend Calculator — DRIP, Yield & Passive Income Forecasts | DPP",
    description:
      "Free dividend reinvestment calculator. Model DRIP compounding, forecast monthly and annual income, and run tax + inflation adjustments.",
    path: "/calculator",
    ogSlug: "calculator",
    jsonLdId: "calculator-jsonld",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Dividend Portfolio Planner — Dividend Calculator",
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web",
      url: "https://dividendportfolioplanner.com/calculator",
      description:
        "Free dividend income projection calculator with DRIP, tax rate, and inflation adjustment. Projects annual dividend income, monthly income, portfolio value, and Yield on Cost over any horizon.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  });

  const [form, setForm] = useState({
    price: 100,
    shares: 10,
    years: 10,
    yieldPct: 3.5,
    annualContribution: 1200,
    drip: true,
    priceGrowthPct: 7,
    divGrowthPct: 6,
    taxPct: 15,
    inflation: false,
  });
  const [activeTicker, setActiveTicker] = useState(null);
  const [customTicker, setCustomTicker] = useState("");
  const [lookingUp, setLookingUp] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Unified ticker apply — always fetches LIVE price + yield from yfinance
  // via /api/tickers/quote so preset chips stay in sync with the market.
  // Growth-rate context (divGrowth, priceGrowth) still comes from
  // TICKER_SEEDS when the ticker is one of our curated symbols — those are
  // historical CAGRs, not point-in-time prices, so caching them is fine.
  const applyTicker = async (sym) => {
    const symbol = (sym || "").toUpperCase().trim();
    if (!symbol) return;
    const seed = TICKER_SEEDS[symbol];
    setActiveTicker(symbol);
    setLookingUp(true);
    try {
      const r = await api.get(`/tickers/quote/${encodeURIComponent(symbol)}`);
      const q = r.data;
      setForm((f) => ({
        ...f,
        price: +Number(q.price || (seed?.price || 0)).toFixed(2),
        yieldPct: +Number(q.yield_pct || (seed?.yieldPct || 0)).toFixed(2),
        // Growth rates: pull from the curated seed if available, else keep current.
        ...(seed ? { divGrowthPct: seed.divGrowthPct, priceGrowthPct: seed.priceGrowthPct } : {}),
      }));
      toast.success(`${symbol} · $${Number(q.price).toFixed(2)} · ${Number(q.yield_pct).toFixed(2)}% yield`);
      document.getElementById("calc-inputs")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      // On live-fetch failure for a preset ticker, fall back to the seed
      // rather than leaving the user with nothing.
      if (seed) {
        setForm((f) => ({
          ...f,
          price: seed.price, yieldPct: seed.yieldPct,
          divGrowthPct: seed.divGrowthPct, priceGrowthPct: seed.priceGrowthPct,
        }));
        toast.success(`${symbol} preset loaded (live quote unavailable)`);
        document.getElementById("calc-inputs")?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        toast.error(err?.response?.status === 404 ? `${symbol} not found` : "Lookup failed");
      }
    } finally {
      setLookingUp(false);
    }
  };

  // Kept as a compat wrapper — callers that only have the symbol still work.
  const lookupTicker = applyTicker;

  const { rows } = useMemo(() => project(form), [form]);
  const final = rows[rows.length - 1] || {};

  // Composition breakdown — every part comes from the same projection row, so all four share one
  // basis (inflation-adjusted when that toggle is on). Dividends only count toward value with DRIP;
  // without it they're paid out as cash and the projection's growth figure already excludes them.
  const principalPortion  = final.principal || 0;
  const contribsPortion   = final.cumulativeContribs || 0;
  const growthPortion     = Math.max(0, final.growth || 0);
  const dividendsPortion  = form.drip ? (final.cumulativeDivs || 0) : 0;
  const cashDividends     = form.drip ? 0 : (final.cumulativeDivs || 0);
  const totalForPct       = Math.max(1, principalPortion + contribsPortion + growthPortion + dividendsPortion);
  const yoc               = final.yieldOnCost || 0;

  return (
    <div className="max-w-6xl mx-auto px-6 py-14" data-testid="calculator-page">
      {/* ------------------------------------------------------------
          TICKER TAPE — live dividend snapshot for popular symbols.
          Marquee animation uses CSS keyframes defined in index.css.
      ------------------------------------------------------------ */}
      <TickerTape onTickerClick={lookupTicker} />

      {/* ------------------------------------------------------------
          HERO
      ------------------------------------------------------------ */}
      <div className="text-center max-w-3xl mx-auto mt-8">
        <div className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.25em] text-secondary">
          <CalcIcon className="w-3.5 h-3.5" /> Dividend Calculator
        </div>
        <h1 className="serif text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-tight leading-[1.05]" data-testid="calc-heading">
          Easy Dividend Forecasting
        </h1>
        <p className="text-muted-foreground mt-5 text-base sm:text-lg leading-relaxed">
          Model DRIP compounding across 1–30 years, factor in tax and inflation, and see your projected annual income and Yield on Cost year by year. No sign-up required.
        </p>

        {/* Custom ticker input — uses the shared TickerAutocomplete which
            hits /api/tickers/search (curated dividend universe first,
            yfinance fallback). Picking a suggestion or hitting Enter fires
            lookupTicker with a live /api/tickers/quote call. */}
        <div className="mt-6 flex items-center gap-2 max-w-sm mx-auto" data-testid="calc-custom-ticker-form">
          <div className="flex-1">
            <TickerAutocomplete
              value={customTicker}
              onChange={(v) => setCustomTicker((v || "").toUpperCase())}
              onSelect={(row) => lookupTicker((row?.ticker || "").toUpperCase())}
              placeholder="Enter any ticker (e.g. AAPL)"
              disabled={lookingUp}
              testId="calc-custom-ticker"
            />
          </div>
          <Button
            type="button"
            onClick={() => lookupTicker(customTicker)}
            disabled={lookingUp || !customTicker.trim()}
            className="rounded-full shrink-0"
            data-testid="calc-custom-ticker-submit"
          >
            {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : "Load"}
          </Button>
        </div>

        <div className="mt-5 flex flex-wrap justify-center gap-2 text-xs mono uppercase tracking-widest text-muted-foreground">
          {["SCHD","JEPI","JEPQ","QQQI","O","VYM","MSFT","KO","PEP","VIG"].map((t) => (
            <button
              key={t}
              onClick={() => applyTicker(t)}
              className={`px-3 py-1 rounded-full border transition ${
                activeTicker === t
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border hover:border-primary/40 hover:text-foreground"
              }`}
              data-testid={`hero-ticker-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* ------------------------------------------------------------
          INPUTS + RESULTS
      ------------------------------------------------------------ */}
      <div id="calc-inputs" className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Inputs */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="mono text-[10px] uppercase tracking-widest text-secondary">Inputs</div>
              {activeTicker && (
                <div className="mono text-[10px] uppercase tracking-widest text-primary">
                  Preset · {activeTicker}
                </div>
              )}
            </div>

            <FieldRow label="Unit / stock price" icon={DollarSign}>
              <Stepper value={form.price} onChange={set("price")} step={1} min={0.01} prefix="$" testId="calc-price" />
            </FieldRow>
            <FieldRow label="Number of stocks" icon={LineIcon}>
              <Stepper value={form.shares} onChange={set("shares")} step={1} min={0} testId="calc-shares" />
            </FieldRow>
            <FieldRow label={`Holding period · ${form.years} years`} icon={Calendar}>
              <input
                type="range" min={1} max={30} step={1} value={form.years}
                onChange={(e) => set("years")(Number(e.target.value))}
                className="w-full accent-[hsl(var(--primary))]"
                data-testid="calc-years"
              />
            </FieldRow>
            <FieldRow label="Annual dividend yield" icon={Percent}>
              <Stepper value={form.yieldPct} onChange={set("yieldPct")} step={0.1} min={0} suffix="%" testId="calc-yield" />
            </FieldRow>
            <FieldRow label="Annual contribution" icon={DollarSign}>
              <Stepper value={form.annualContribution} onChange={set("annualContribution")} step={100} min={0} prefix="$" testId="calc-contribution" />
            </FieldRow>

            <FieldRow label="Dividend reinvestment (DRIP)" icon={Repeat}>
              <Segmented
                value={form.drip}
                onChange={set("drip")}
                options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
                testId="calc-drip"
              />
            </FieldRow>
            <FieldRow label="Expected stock appreciation" icon={TrendingUp}>
              <Stepper value={form.priceGrowthPct} onChange={set("priceGrowthPct")} step={0.5} suffix="%" testId="calc-price-growth" />
            </FieldRow>
            <FieldRow label="Expected dividend growth" icon={TrendingUp}>
              <Stepper value={form.divGrowthPct} onChange={set("divGrowthPct")} step={0.5} suffix="%" testId="calc-div-growth" />
            </FieldRow>
            <FieldRow label="Tax rate on dividends" icon={BadgePercent}>
              <Stepper value={form.taxPct} onChange={set("taxPct")} step={1} min={0} max={40} suffix="%" testId="calc-tax" />
            </FieldRow>
            <FieldRow label="Adjust for inflation (3%)" icon={Info}>
              <Segmented
                value={form.inflation}
                onChange={set("inflation")}
                options={[{ label: "No", value: false }, { label: "Yes", value: true }]}
                testId="calc-inflation"
              />
            </FieldRow>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="lg:col-span-3 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total Value" value={fmt(final.value)} accent testId="calc-total-value" />
            <Stat label="Annual Div." value={fmt(final.annualIncome)} accent testId="calc-annual-income" />
            <Stat label="Monthly Div." value={fmt(final.monthlyIncome)} testId="calc-monthly-income" />
            <Stat label="Yield on Cost" value={pct(yoc)} testId="calc-yoc" />
          </div>

          {/* Composition breakdown */}
          <Card>
            <CardContent className="p-5">
              <div className="mono text-[10px] uppercase tracking-widest text-secondary mb-3">Value composition</div>
              <CompositionBar
                parts={[
                  { key: "Principal",     value: principalPortion, color: "bg-slate-500" },
                  { key: "Contributions", value: contribsPortion,  color: "bg-blue-500" },
                  { key: "Growth",        value: growthPortion,    color: "bg-emerald-500" },
                  { key: "Dividends",     value: dividendsPortion, color: "bg-[hsl(var(--secondary))]" },
                ]}
                total={totalForPct}
              />
              {cashDividends > 0 && (
                <p className="text-xs text-muted-foreground mt-3" data-testid="calc-cash-dividends">
                  Plus {fmt(cashDividends)} of dividends paid out as cash (DRIP off), not included in the value above.
                </p>
              )}
              {form.inflation && <p className="text-[11px] text-muted-foreground mt-1">All parts shown in today&apos;s dollars (inflation-adjusted).</p>}
            </CardContent>
          </Card>

          {/* Per-year annual dividend bar chart */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="mono text-[10px] uppercase tracking-widest text-secondary">Annual dividend income by year</div>
                <button
                  onClick={() => downloadCSV(rows)}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition"
                  data-testid="calc-download-csv"
                >
                  <Download className="w-3.5 h-3.5" /> Download CSV
                </button>
              </div>
              <div className="h-72" data-testid="calc-bar-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rows} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `Y${v}`} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `$${(v/1000).toFixed(v >= 1000 ? 0 : 1)}k`} />
                    <Tooltip
                      formatter={(v) => [fmt(v), "Annual dividend"]}
                      labelFormatter={(l) => `Year ${l}`}
                      contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.5rem" }}
                    />
                    <Bar dataKey="annualIncome" fill="hsl(var(--secondary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* DRIP vs No-DRIP comparison chart */}
          <SnowballChart form={form} />

          {/* CTA */}
          <Card className="border-primary/25 bg-primary/[0.03]">
            <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <div>
                <div className="serif text-xl">Ready to backtest with real 30-year data?</div>
                <div className="text-sm text-muted-foreground mt-1">This calculator uses assumed rates. The DPP builder runs your portfolio against actual historical prices and dividends — with an AI explanation of why the numbers look the way they do.</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button className="rounded-full" onClick={() => nav("/portfolio/new")} data-testid="calc-cta-backtest">
                  <Sparkles className="w-4 h-4 mr-2" /> Run a Free Backtest
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => nav("/pricing")}>
                  Pricing <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ------------------------------------------------------------
          BROWSE BY STRATEGY
      ------------------------------------------------------------ */}
      <section className="mt-20" data-testid="strategy-browser">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="serif text-3xl sm:text-4xl tracking-tight">Browse by investment strategy</h2>
          <p className="text-muted-foreground mt-3 text-sm">Click any ticker to pre-fill the calculator with a recent price, yield, and dividend growth rate.</p>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {STRATEGIES.map((s) => (
            <Card key={s.title} className="h-full">
              <CardContent className="p-5">
                <div className="text-2xl leading-none">{s.icon}</div>
                <div className="serif text-lg mt-3">{s.title}</div>
                <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.desc}</div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {s.tickers.map((t) => (
                    <button
                      key={t}
                      onClick={() => applyTicker(t)}
                      className="mono text-[11px] tracking-wider px-2.5 py-1 rounded-full border border-border hover:border-primary hover:text-primary transition"
                      data-testid={`strategy-ticker-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------
          FEATURE CALLOUT — What the Calculator Models
      ------------------------------------------------------------ */}
      <section className="mt-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="serif text-3xl sm:text-4xl tracking-tight">What the calculator actually models</h2>
          <p className="text-muted-foreground mt-3 text-sm">Generic tools multiply shares by yield. DPP models the full compounding cycle.</p>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FeatureCard
            icon={Repeat}
            title="DRIP reinvestment model"
            body="Compare portfolio values with DRIP enabled and disabled on the same chart across 1, 5, 10, or 30-year horizons."
          />
          <FeatureCard
            icon={BadgePercent}
            title="Dividend tax rate (0–40%)"
            body="Set your effective dividend tax rate from 0% (tax-advantaged accounts) to 40%. See true after-tax compounding projections."
          />
          <FeatureCard
            icon={LineIcon}
            title="Snowball comparison chart"
            body="The DRIP vs no-DRIP chart maps portfolio value divergence — the gap widens dramatically after year 10."
          />
          <FeatureCard
            icon={ShieldCheck}
            title="Yield on Cost tracking"
            body="Watch your Yield on Cost climb as dividend growth compounds — the single number that separates income compounders from yield chasers."
          />
        </div>
      </section>

      {/* ------------------------------------------------------------
          COMPARISON TABLE
      ------------------------------------------------------------ */}
      <section className="mt-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="serif text-3xl sm:text-4xl tracking-tight">DPP calculator vs. generic tools</h2>
          <p className="text-muted-foreground mt-3 text-sm">Not all dividend calculators are created equal.</p>
        </div>
        <Card className="mt-8">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-5 py-4 mono text-[10px] uppercase tracking-widest text-muted-foreground">Feature</th>
                    <th className="text-center px-5 py-4 mono text-[10px] uppercase tracking-widest text-primary">DPP</th>
                    <th className="text-center px-5 py-4 mono text-[10px] uppercase tracking-widest text-muted-foreground">Free online tools</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    "DRIP reinvestment at projected price",
                    "Historical dividend CAGR per asset",
                    "Adjustable dividend tax rate (0–40%)",
                    "Inflation-adjusted (real return) view",
                    "Full 30-year DRIP vs. no-DRIP chart",
                    "Downloadable per-year CSV",
                    "Full portfolio tracking + brokerage sync (SnapTrade)",
                    "AI explanation of backtest results",
                  ].map((row, i) => (
                    <tr key={row} className={i % 2 ? "bg-muted/30" : ""}>
                      <td className="px-5 py-3">{row}</td>
                      <td className="px-5 py-3 text-center"><Check className="w-4 h-4 text-primary inline" /></td>
                      <td className="px-5 py-3 text-center"><X className="w-4 h-4 text-muted-foreground inline" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* ------------------------------------------------------------
          CONCEPTS
      ------------------------------------------------------------ */}
      <section className="mt-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="serif text-3xl sm:text-4xl tracking-tight">Dividend investing concepts explained</h2>
          <p className="text-muted-foreground mt-3 text-sm">Key ideas to understand before running long-term DRIP projections.</p>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConceptCard tag="Concept" title="Understanding Yield on Cost" body="Your initial yield is your entry point. Yield on Cost is what you earn on your original capital after years of dividend growth. A 3% entry into KO in 2005 yields over 15% on cost today." />
          <ConceptCard tag="Risk" title="The danger of yield traps" body="A dividend cut from 8% to 4% destroys your capital twice — income drops and the stock price collapses. Watch for payout ratios above 80% or rising debt. A sustainable 3% beats a fragile 9%." />
          <ConceptCard tag="Strategy" title="Dividend tax & compounding" body="What you pay in dividend taxes reduces the income you reinvest. Compare a 0% rate in a tax-advantaged account vs. 15–30% in a taxable one — the drag compounds over 20+ years." />
        </div>
      </section>

      {/* ------------------------------------------------------------
          FAQ
      ------------------------------------------------------------ */}
      <section className="mt-20 max-w-3xl mx-auto" data-testid="calc-faq">
        <div className="text-center">
          <h2 className="serif text-3xl sm:text-4xl tracking-tight">Frequently asked questions</h2>
        </div>
        <Accordion type="single" collapsible className="mt-8">
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <div className="mt-20">
        <AboutDppCard />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Small sub-components
// ------------------------------------------------------------------
function FieldRow({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="mb-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </Label>
      {children}
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  // Font-size auto-shrinks based on rendered character length so values like
  // "$1,234,567" and "$12,345,678" never overflow the card. Ranges tuned
  // against the actual `fmt()` and `pct()` outputs used elsewhere in the
  // calculator. `min-w-0` on the wrapping Card + `break-words` on the value
  // are the belt-and-braces fallback for anything truly extreme.
  const len = String(value ?? "").length;
  const valueSize =
    len <= 6  ? "text-2xl sm:text-3xl"  :
    len <= 8  ? "text-xl sm:text-2xl"   :
    len <= 11 ? "text-lg sm:text-xl"    :
    len <= 14 ? "text-base sm:text-lg"  :
                "text-sm sm:text-base";
  return (
    <Card className={`min-w-0 ${accent ? "border-primary/25 bg-primary/[0.03]" : ""}`}>
      <CardContent className="p-4 min-w-0">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground truncate">{label}</div>
        <div
          className={`serif ${valueSize} mt-1 tabular-nums leading-tight break-words ${accent ? "text-primary" : ""}`}
          data-testid={testId}
          title={String(value ?? "")}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function CompositionBar({ parts, total }) {
  const safeTotal = total <= 0 ? 1 : total;
  return (
    <div>
      <div className="flex h-3 rounded-full overflow-hidden bg-muted">
        {parts.map((p) => {
          const w = Math.max(0, (p.value / safeTotal) * 100);
          if (w <= 0) return null;
          return <div key={p.key} className={p.color} style={{ width: `${w}%` }} title={`${p.key}: ${fmt(p.value)}`} />;
        })}
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {parts.map((p) => {
          const w = (p.value / safeTotal) * 100;
          return (
            <div key={p.key} className="flex items-start gap-2">
              <span className={`w-2.5 h-2.5 mt-1.5 rounded-full ${p.color} shrink-0`} />
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{p.key}</div>
                <div className="serif text-lg tabular-nums" data-testid={`composition-${p.key.toLowerCase()}`}>{fmt(p.value)}</div>
                <div className="text-[11px] text-muted-foreground tabular-nums">{w.toFixed(1)}%</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SnowballChart({ form }) {
  // Snowball = DRIP on vs off on the same chart. This gives the
  // classic "divergence after year 10" story so users can see the
  // impact of reinvesting for themselves.
  const { rows: dripRows } = useMemo(() => project({ ...form, drip: true }),  [form]);
  const { rows: cashRows } = useMemo(() => project({ ...form, drip: false }), [form]);
  const merged = dripRows.map((r, i) => ({
    year: r.year,
    drip: r.value,
    cash: cashRows[i]?.value ?? 0,
  }));
  const gap = (merged[merged.length - 1]?.drip || 0) - (merged[merged.length - 1]?.cash || 0);

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-secondary">The Snowball Effect</div>
            <div className="text-sm text-muted-foreground mt-1">DRIP vs. taking dividends as cash · gap after year {form.years}: <span className="text-primary font-medium">{fmt(gap)}</span></div>
          </div>
        </div>
        <div className="h-72" data-testid="calc-snowball-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={merged} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `Y${v}`} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(v, n) => [fmt(v), n === "drip" ? "With DRIP" : "Without DRIP"]}
                labelFormatter={(l) => `Year ${l}`}
                contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.5rem" }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => (v === "drip" ? "With DRIP" : "Without DRIP")} />
              <Line type="monotone" dataKey="drip" stroke="hsl(var(--primary))"   strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="cash" stroke="hsl(var(--muted-foreground))" strokeWidth={2} strokeDasharray="4 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="w-9 h-9 rounded-full bg-primary/10 text-primary grid place-items-center">
          <Icon className="w-4 h-4" />
        </div>
        <div className="serif text-lg mt-4">{title}</div>
        <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{body}</div>
      </CardContent>
    </Card>
  );
}

function ConceptCard({ tag, title, body }) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="mono text-[10px] uppercase tracking-widest text-secondary">{tag}</div>
        <div className="serif text-lg mt-3">{title}</div>
        <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{body}</div>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------
// TickerTape — CNBC-style scrolling marquee showing live price + yield
// for a curated dividend universe. Uses /api/tickers/quote/{ticker}
// (public, no auth) and refreshes on mount. Clicking any tile pre-fills
// the calculator with that ticker's live snapshot.
// ------------------------------------------------------------------
const TAPE_SYMBOLS = [
  "SCHD", "JEPI", "JEPQ", "QQQI", "O", "VYM", "VIG", "DGRO",
  "MSFT", "AVGO", "KO", "PEP", "JNJ", "PG", "XOM", "CVX", "MAIN", "STAG",
];

function TickerTape({ onTickerClick }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Fire all requests in parallel — tape is best-effort so we don't await sequentially.
      const results = await Promise.allSettled(
        TAPE_SYMBOLS.map((s) => api.get(`/tickers/quote/${s}`).then((r) => r.data))
      );
      if (cancelled) return;
      const good = results
        .filter((r) => r.status === "fulfilled" && r.value?.price)
        .map((r) => r.value);
      setQuotes(good);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-full border border-border bg-muted/30 py-2 px-4 text-xs mono uppercase tracking-widest text-muted-foreground flex items-center gap-2" data-testid="ticker-tape-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading live dividend snapshot…
      </div>
    );
  }
  if (!quotes.length) return null;

  // Duplicate the list so the marquee loop is seamless
  const doubled = [...quotes, ...quotes];

  return (
    <div
      className="relative rounded-full border border-border bg-card overflow-hidden group"
      data-testid="ticker-tape"
    >
      {/* left/right fades */}
      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-card to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-card to-transparent z-10 pointer-events-none" />
      <div className="flex whitespace-nowrap animate-ticker-tape group-hover:[animation-play-state:paused]">
        {doubled.map((q, i) => (
          <button
            key={`${q.ticker}-${i}`}
            onClick={() => onTickerClick(q.ticker)}
            className="inline-flex items-center gap-2 px-4 py-3 border-r border-border hover:bg-muted/50 transition shrink-0"
            data-testid={`tape-${q.ticker}-${i < quotes.length ? "a" : "b"}`}
          >
            <span className="mono text-[11px] tracking-wider font-medium text-foreground">{q.ticker}</span>
            <span className="text-sm tabular-nums text-foreground">${Number(q.price).toFixed(2)}</span>
            <span className={`text-xs tabular-nums ${Number(q.yield_pct) > 0 ? "text-secondary" : "text-muted-foreground"}`}>
              {Number(q.yield_pct).toFixed(2)}%
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

